import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalBody,
  DropdownItem,
  Form,
} from "reactstrap";
import {
  Block,
  BlockHead,
  BlockBetween,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
  UserAvatar,
  RSelect,
  Col,
} from "../../../components/Component";
import { useForm } from "react-hook-form";
import { api } from "../../../utils/api";
import { findUpper } from "../../../utils/Utils";
import { toast } from "react-toastify";
import { Badge, Spinner, Input, Label } from "reactstrap";
import { PreviewCard } from "../../../components/Component";

const UserManagementPage = () => {
  const [sm, updateSm] = useState(false);
  const [modal, setModal] = useState({
    edit: false,
    add: false,
    view: false,
  });
  const [viewUser, setViewUser] = useState(null);
  const [editId, setEditedId] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ status: "all" });
  const [deleteModal, setDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    role: "",
    site_ids: [],
    is_active: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [generatingPasswordUserId, setGeneratingPasswordUserId] = useState(null);
  const [generatedPasswordModal, setGeneratedPasswordModal] = useState({
    open: false,
    userName: "",
    password: "",
  });
  const [generatePasswordConfirmModal, setGeneratePasswordConfirmModal] = useState({
    open: false,
    user: null,
  });

  useEffect(() => {
    loadUsers();
    loadSites();
  }, [filters, currentPage, searchText]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemPerPage,
      };
      
      if (filters.status && filters.status !== "all") {
        params.status = filters.status;
      }

      if (searchText) {
        params.search = searchText;
      }

      const response = await api.getUsers(params);
      
      if (response.data) {
        setData(response.data);
        setTotalItems(response.total || 0);
      } else {
        setData(response);
        setTotalItems(response.length || 0);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const response = await api.getSites();
      setSites(response.data || response || []);
    } catch (error) {
      // Ignorer l'erreur silencieusement
    }
  };

  const onInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      role: "",
      site_ids: [],
      is_active: true,
    });
  };

  const onFormCancel = () => {
    setModal({ edit: false, add: false, view: false });
    resetForm();
  };

  const onFormSubmit = async (sData) => {
    try {
      const formattedData = {
        ...formData,
        ...sData,
        site_ids: formData.site_ids || [],
      };

      await api.createUser(formattedData);
      toast.success("Utilisateur créé avec succès");
      loadUsers();
      resetForm();
      setModal({ add: false });
    } catch (error) {
      let errorMessage = "Erreur lors de la création de l'utilisateur";
      if (error.errors) {
        const errorsList = Object.values(error.errors).flat().join(', ');
        errorMessage = errorsList || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const onEditClick = async (id) => {
    try {
      const user = await api.getUser(id);
      const userData = user.data || user;
      
      // Récupérer le rôle principal
      const mainRole = userData.roles && userData.roles.length > 0 ? userData.roles[0].name : "";
      const roleValue = mapRoleToValue(mainRole);
      
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        password: "",
        role: roleValue,
        site_ids: userData.sites ? userData.sites.map(s => s.id) : [],
        is_active: userData.is_active !== undefined ? userData.is_active : true,
      });
      setModal({ edit: true, add: false });
      setEditedId(id);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'utilisateur");
    }
  };

  const mapRoleToValue = (roleName) => {
    const roleMap = {
      'Employé': 'employee',
      'Superviseur': 'supervisor',
      'Responsable HSE': 'hse_manager',
      'Manager': 'manager',
      'Manager / Direction': 'manager',
      'Administrateur système': 'admin',
    };
    return roleMap[roleName] || 'employee';
  };

  const onEditSubmit = async (sData) => {
    try {
      const formattedData = {
        ...formData,
        ...sData,
        site_ids: formData.site_ids || [],
      };

      // Ne pas envoyer le mot de passe s'il est vide
      if (!formattedData.password) {
        delete formattedData.password;
      }

      await api.updateUser(editId, formattedData);
      toast.success("Utilisateur mis à jour avec succès");
      loadUsers();
      resetForm();
      setModal({ edit: false });
    } catch (error) {
      let errorMessage = "Erreur lors de la mise à jour de l'utilisateur";
      if (error.errors) {
        const errorsList = Object.values(error.errors).flat().join(', ');
        errorMessage = errorsList || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const selectorCheck = (e) => {
    const newData = data.map((item) => {
      item.checked = e.currentTarget.checked;
      return item;
    });
    setData([...newData]);
  };

  const onSelectChange = (e, id) => {
    const newData = data;
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].checked = e.currentTarget.checked;
      setData([...newData]);
    }
  };

  const onDeleteClick = (id) => {
    const user = data.find((u) => u.id === id);
    setUserToDelete(user);
    setDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await api.deleteUser(userToDelete.id);
      toast.success("Utilisateur supprimé avec succès");
      setDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      toast.error(error.message || "Erreur lors de la suppression de l'utilisateur");
    }
  };

  const onDeleteCancel = () => {
    setDeleteModal(false);
    setUserToDelete(null);
  };

  const onGeneratePasswordClick = (user) => {
    if (!user?.id) return;
    setGeneratePasswordConfirmModal({ open: true, user });
  };

  const onGeneratePasswordConfirm = async () => {
    const user = generatePasswordConfirmModal.user;
    if (!user?.id) return;

    try {
      setGeneratePasswordConfirmModal({ open: false, user: null });
      setGeneratingPasswordUserId(user.id);
      const response = await api.generateUserPassword(user.id);
      const generatedPassword = response?.temporary_password || response?.data?.temporary_password;

      if (!generatedPassword) {
        toast.success("Mot de passe généré, mais la valeur n'a pas été retournée.");
        return;
      }

      setGeneratedPasswordModal({
        open: true,
        userName: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || "Utilisateur",
        password: generatedPassword,
      });
    } catch (error) {
      toast.error(error.message || "Erreur lors de la génération du mot de passe");
    } finally {
      setGeneratingPasswordUserId(null);
    }
  };

  const onGeneratePasswordCancel = () => {
    if (generatingPasswordUserId) return;
    setGeneratePasswordConfirmModal({ open: false, user: null });
  };

  const onCopyGeneratedPassword = async () => {
    const password = generatedPasswordModal.password;
    if (!password) return;

    try {
      await navigator.clipboard.writeText(password);
      toast.success("Mot de passe copié dans le presse-papiers");
    } catch (error) {
      toast.error("Impossible de copier automatiquement. Copiez le mot de passe manuellement.");
    }
  };

  const getRoleBadge = (roles) => {
    if (!roles || roles.length === 0) {
      return <Badge color="secondary">Non défini</Badge>;
    }
    const roleName = roles[0].name;
    const roleColors = {
      'Employé': 'info',
      'Superviseur': 'primary',
      'Responsable HSE': 'success',
      'Manager': 'warning',
      'Manager / Direction': 'warning',
      'Administrateur système': 'danger',
    };
    return <Badge color={roleColors[roleName] || 'secondary'}>{roleName}</Badge>;
  };

  const onViewClick = async (id) => {
    try {
      const user = await api.getUser(id);
      const userData = user.data || user;
      setViewUser(userData);
      setModal({ view: true, edit: false, add: false });
    } catch (error) {
      toast.error("Erreur lors du chargement de l'utilisateur");
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const { errors, register, handleSubmit } = useForm();

  return (
    <React.Fragment>
      <Head title="Gestion des utilisateurs" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Gestion des utilisateurs</BlockTitle>
              <BlockDes className="text-soft">Vous avez {totalItems} utilisateur(s) au total</BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-left">
                          <Icon name="search"></Icon>
                        </div>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Rechercher un utilisateur..."
                          value={searchText}
                          onChange={(e) => {
                            setSearchText(e.target.value);
                            setCurrentPage(1);
                          }}
                          style={{ minWidth: "250px" }}
                        />
                      </div>
                    </li>
                    <li>
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="dropdown-toggle btn btn-white btn-dim btn-outline-light">
                          <Icon name="filter-alt" className="d-none d-sm-inline"></Icon>
                          <span>Filtrer</span>
                          <Icon name="chevron-right" className="dd-indc"></Icon>
                        </DropdownToggle>
                        <DropdownMenu end>
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ ...filters, status: "all" });
                                  setCurrentPage(1);
                                }}
                              >
                                <span>Tous</span>
                              </DropdownItem>
                            </li>
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ ...filters, status: "active" });
                                  setCurrentPage(1);
                                }}
                              >
                                <span>Actifs</span>
                              </DropdownItem>
                            </li>
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ ...filters, status: "inactive" });
                                  setCurrentPage(1);
                                }}
                              >
                                <span>Inactifs</span>
                              </DropdownItem>
                            </li>
                          </ul>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                    <li className="nk-block-tools-opt" onClick={() => setModal({ add: true })}>
                      <Button color="primary">
                        <Icon name="plus"></Icon>
                        <span>Nouvel utilisateur</span>
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <DataTable className="card-stretch">
            <DataTableBody>
              <DataTableHead className="nk-tb-item nk-tb-head">
                <DataTableRow className="nk-tb-col-check">
                  <div className="custom-control custom-control-sm custom-checkbox notext">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="user-all"
                      onChange={(e) => selectorCheck(e)}
                    />
                    <label className="custom-control-label" htmlFor="user-all"></label>
                  </div>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Utilisateur</span>
                </DataTableRow>
                <DataTableRow size="xl">
                  <span className="sub-text">Rôle</span>
                </DataTableRow>
                <DataTableRow size="xxl">
                  <span className="sub-text">Site</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Statut</span>
                </DataTableRow>
                <DataTableRow className="nk-tb-col-tools text-end">
                  <UncontrolledDropdown>
                    <DropdownToggle tag="a" className="btn btn-xs btn-trigger btn-icon dropdown-toggle me-n1">
                      <Icon name="more-h"></Icon>
                    </DropdownToggle>
                    <DropdownMenu end>
                      <ul className="link-list-opt no-bdr">
                        <li>
                          <DropdownItem
                            tag="a"
                            href="#remove"
                            onClick={(ev) => {
                              ev.preventDefault();
                            }}
                          >
                            <Icon name="trash"></Icon>
                            <span>Supprimer</span>
                          </DropdownItem>
                        </li>
                      </ul>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </DataTableRow>
              </DataTableHead>
              {loading ? (
                <DataTableItem>
                  <DataTableRow>
                    <DataTableItem colSpan={6} className="text-center">
                      <Spinner color="primary" />
                    </DataTableItem>
                  </DataTableRow>
                </DataTableItem>
              ) : data.length === 0 ? (
                <DataTableItem>
                  <DataTableRow>
                    <DataTableItem colSpan={6} className="text-center">
                      <PreviewCard>
                        <div className="text-center py-4">
                          <span className="text-soft">Aucun utilisateur trouvé</span>
                        </div>
                      </PreviewCard>
                    </DataTableItem>
                  </DataTableRow>
                </DataTableItem>
              ) : (
                data.map((item) => (
                  <DataTableItem key={item.id}>
                    <DataTableRow className="nk-tb-col-check">
                      <div className="custom-control custom-control-sm custom-checkbox notext">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          defaultChecked={item.checked}
                          id={item.id + "user"}
                          key={Math.random()}
                          onChange={(e) => onSelectChange(e, item.id)}
                        />
                        <label className="custom-control-label" htmlFor={item.id + "user"}></label>
                      </div>
                    </DataTableRow>
                    <DataTableRow>
                      <a
                        href="#title"
                        onClick={(ev) => {
                          ev.preventDefault();
                        }}
                        className="project-title"
                      >
                        <UserAvatar
                          className="sq"
                          theme="blue"
                          text={findUpper(`${item.first_name || ""} ${item.last_name || ""}`.trim() || "N/A")}
                        />
                        <div className="project-info">
                          <h6 className="title">{`${item.first_name || ""} ${item.last_name || ""}`.trim() || "N/A"}</h6>
                          <span className="sub-text">{item.email || ""}</span>
                        </div>
                      </a>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      {getRoleBadge(item.roles)}
                    </DataTableRow>
                    <DataTableRow size="xxl">
                      <span>
                        {item.sites && item.sites.length > 0
                          ? item.sites.map(s => s.name).join(", ")
                          : "Aucun site"}
                      </span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <Badge color={item.is_active ? "success" : "danger"}>
                        {item.is_active ? "Actif" : "Inactif"}
                      </Badge>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <ul className="nk-tb-actions gx-1">
                        <li>
                          <UncontrolledDropdown>
                            <DropdownToggle tag="a" className="text-soft dropdown-toggle btn btn-icon btn-trigger">
                              <Icon name="more-h"></Icon>
                            </DropdownToggle>
                            <DropdownMenu end>
                              <ul className="link-list-opt no-bdr">
                                <li onClick={() => onViewClick(item.id)}>
                                  <DropdownItem
                                    tag="a"
                                    href="#view"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                    }}
                                  >
                                    <Icon name="eye"></Icon>
                                    <span>Voir</span>
                                  </DropdownItem>
                                </li>
                                <li onClick={() => onEditClick(item.id)}>
                                  <DropdownItem
                                    tag="a"
                                    href="#edit"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                    }}
                                  >
                                    <Icon name="edit"></Icon>
                                    <span>Modifier</span>
                                  </DropdownItem>
                                </li>
                                <li onClick={() => onDeleteClick(item.id)}>
                                  <DropdownItem
                                    tag="a"
                                    href="#delete"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                    }}
                                  >
                                    <Icon name="trash"></Icon>
                                    <span>Supprimer</span>
                                  </DropdownItem>
                                </li>
                                <li onClick={() => onGeneratePasswordClick(item)}>
                                  <DropdownItem
                                    tag="a"
                                    href="#generate-password"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                    }}
                                  >
                                    <Icon name="lock-alt"></Icon>
                                    <span>
                                      {generatingPasswordUserId === item.id
                                        ? "Génération en cours..."
                                        : "Générer mot de passe"}
                                    </span>
                                  </DropdownItem>
                                </li>
                              </ul>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </li>
                      </ul>
                    </DataTableRow>
                  </DataTableItem>
                ))
              )}
            </DataTableBody>
            <div className="card-inner">
              {totalItems > 0 && (
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={totalItems}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              )}
            </div>
          </DataTable>
        </Block>

        <Modal isOpen={modal.add} toggle={() => setModal({ add: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Nouvel utilisateur</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onFormSubmit)}>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Prénom</label>
                      <input
                        type="text"
                        name="first_name"
                        defaultValue={formData.first_name}
                        placeholder="Entrez le prénom"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "Ce champ est requis",
                        })}
                      />
                      {errors.first_name && <span className="invalid">{errors.first_name.message}</span>}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Nom</label>
                      <input
                        type="text"
                        name="last_name"
                        defaultValue={formData.last_name}
                        placeholder="Entrez le nom"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "Ce champ est requis",
                        })}
                      />
                      {errors.last_name && <span className="invalid">{errors.last_name.message}</span>}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        defaultValue={formData.email}
                        placeholder="Entrez l'email"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "Ce champ est requis",
                        })}
                      />
                      {errors.email && <span className="invalid">{errors.email.message}</span>}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Mot de passe *</label>
                      <input
                        type="password"
                        name="password"
                        defaultValue={formData.password}
                        placeholder="Entrez le mot de passe"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "Le mot de passe est requis",
                          minLength: {
                            value: 8,
                            message: "Le mot de passe doit contenir au moins 8 caractères",
                          },
                        })}
                      />
                      {errors.password && <span className="invalid">{errors.password.message}</span>}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Rôle *</label>
                      <RSelect
                        options={[
                          { value: "employee", label: "Employé" },
                          { value: "supervisor", label: "Superviseur" },
                          { value: "hse_manager", label: "Responsable HSE" },
                          { value: "manager", label: "Manager / Direction" },
                          { value: "admin", label: "Administrateur système" },
                        ]}
                        value={formData.role ? { value: formData.role, label: formData.role === "employee" ? "Employé" : formData.role === "supervisor" ? "Superviseur" : formData.role === "hse_manager" ? "Responsable HSE" : formData.role === "manager" ? "Manager / Direction" : "Administrateur système" } : null}
                        onChange={(e) => setFormData({ ...formData, role: e.value })}
                      />
                    </div>
                  </Col>
                  <Col md="12">
                    <div className="form-group">
                      <label className="form-label">Sites</label>
                      <RSelect
                        isMulti
                        options={sites.map(site => ({ value: site.id, label: site.name }))}
                        value={formData.site_ids ? formData.site_ids.map(id => {
                          const site = sites.find(s => s.id === id);
                          return site ? { value: site.id, label: site.name } : null;
                        }).filter(Boolean) : []}
                        onChange={(selected) => {
                          setFormData({
                            ...formData,
                            site_ids: selected ? selected.map(opt => opt.value) : [],
                          });
                        }}
                        placeholder="Sélectionner les sites"
                      />
                    </div>
                  </Col>
                  <Col md="12">
                    <div className="form-group">
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="is_active_add"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                        <label className="custom-control-label" htmlFor="is_active_add">
                          Utilisateur actif
                        </label>
                      </div>
                    </div>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 justify-content-end">
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light me-2"
                        >
                          Annuler
                        </Button>
                      </li>
                      <li>
                        <Button color="primary" size="md" type="submit">
                          Ajouter
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal isOpen={modal.edit} toggle={() => setModal({ edit: false })} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a
              href="#cancel"
              onClick={(ev) => {
                ev.preventDefault();
                onFormCancel();
              }}
              className="close"
            >
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="p-2">
              <h5 className="title">Modifier l'utilisateur</h5>
              <div className="mt-4">
                <Form className="row gy-4" onSubmit={handleSubmit(onEditSubmit)}>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Prénom</label>
                      <input
                        type="text"
                        name="first_name"
                        defaultValue={formData.first_name}
                        placeholder="Entrez le prénom"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "Ce champ est requis",
                        })}
                      />
                      {errors.first_name && <span className="invalid">{errors.first_name.message}</span>}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Nom</label>
                      <input
                        type="text"
                        name="last_name"
                        defaultValue={formData.last_name}
                        placeholder="Entrez le nom"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "Ce champ est requis",
                        })}
                      />
                      {errors.last_name && <span className="invalid">{errors.last_name.message}</span>}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        defaultValue={formData.email}
                        placeholder="Entrez l'email"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          required: "Ce champ est requis",
                        })}
                      />
                      {errors.email && <span className="invalid">{errors.email.message}</span>}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Mot de passe</label>
                      <input
                        type="password"
                        name="password"
                        defaultValue={formData.password}
                        placeholder="Laisser vide pour ne pas modifier"
                        onChange={(e) => onInputChange(e)}
                        className="form-control"
                        ref={register({
                          minLength: {
                            value: 8,
                            message: "Le mot de passe doit contenir au moins 8 caractères",
                          },
                        })}
                      />
                      {errors.password && <span className="invalid">{errors.password.message}</span>}
                      <small className="text-soft">Laisser vide pour ne pas modifier le mot de passe</small>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label className="form-label">Rôle *</label>
                      <RSelect
                        options={[
                          { value: "employee", label: "Employé" },
                          { value: "supervisor", label: "Superviseur" },
                          { value: "hse_manager", label: "Responsable HSE" },
                          { value: "manager", label: "Manager / Direction" },
                          { value: "admin", label: "Administrateur système" },
                        ]}
                        value={formData.role ? { value: formData.role, label: formData.role === "employee" ? "Employé" : formData.role === "supervisor" ? "Superviseur" : formData.role === "hse_manager" ? "Responsable HSE" : formData.role === "manager" ? "Manager / Direction" : "Administrateur système" } : null}
                        onChange={(e) => setFormData({ ...formData, role: e.value })}
                      />
                    </div>
                  </Col>
                  <Col md="12">
                    <div className="form-group">
                      <label className="form-label">Sites</label>
                      <RSelect
                        isMulti
                        options={sites.map(site => ({ value: site.id, label: site.name }))}
                        value={formData.site_ids ? formData.site_ids.map(id => {
                          const site = sites.find(s => s.id === id);
                          return site ? { value: site.id, label: site.name } : null;
                        }).filter(Boolean) : []}
                        onChange={(selected) => {
                          setFormData({
                            ...formData,
                            site_ids: selected ? selected.map(opt => opt.value) : [],
                          });
                        }}
                        placeholder="Sélectionner les sites"
                      />
                    </div>
                  </Col>
                  <Col md="12">
                    <div className="form-group">
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="is_active_edit"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                        <label className="custom-control-label" htmlFor="is_active_edit">
                          Utilisateur actif
                        </label>
                      </div>
                    </div>
                  </Col>
                  <Col size="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 justify-content-end">
                      <li>
                        <Button
                          onClick={(ev) => {
                            ev.preventDefault();
                            onFormCancel();
                          }}
                          className="link link-light me-2"
                        >
                          Annuler
                        </Button>
                      </li>
                      <li>
                        <Button color="primary" size="md" type="submit">
                          Mettre à jour
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Form>
              </div>
            </div>
          </ModalBody>
        </Modal>

        {/* Modal Voir utilisateur */}
        <Modal isOpen={modal.view} toggle={() => setModal({ view: false })} className="modal-dialog-centered" size="lg">
          {viewUser && (
            <>
              <div className="modal-header border-bottom">
                <div className="d-flex align-items-center justify-content-between w-100">
                  <div className="d-flex align-items-center">
                    <UserAvatar
                      className="me-3"
                      size="lg"
                      theme="blue"
                      text={findUpper(`${viewUser.first_name || ""} ${viewUser.last_name || ""}`.trim() || "N/A")}
                    />
                    <div>
                      <h5 className="modal-title mb-1">
                        {`${viewUser.first_name || ""} ${viewUser.last_name || ""}`.trim() || "N/A"}
                      </h5>
                      <p className="text-soft mb-0">{viewUser.email || ""}</p>
                    </div>
                  </div>
                  <button className="close" onClick={() => setModal({ view: false })}>
                    <Icon name="cross"></Icon>
                  </button>
                </div>
                <div className="d-flex align-items-center mt-3">
                  {getRoleBadge(viewUser.roles)}
                  <Badge className="ms-2" color={viewUser.is_active ? "success" : "danger"}>
                    {viewUser.is_active ? "Actif" : "Inactif"}
                  </Badge>
                </div>
              </div>
              <ModalBody className="p-4">
                <div className="row g-4">
                  {/* Informations principales */}
                  <Col md="12">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-4">
                        <h6 className="title mb-3">
                          <Icon name="user-alt" className="me-2"></Icon>
                          Informations personnelles
                        </h6>
                        <div className="row g-3">
                          <Col md="6">
                            <div className="d-flex align-items-start">
                              <Icon name="user" className="text-primary me-2 mt-1"></Icon>
                              <div>
                                <span className="sub-text d-block">Prénom</span>
                                <span className="caption-text">{viewUser.first_name || "N/A"}</span>
                              </div>
                            </div>
                          </Col>
                          <Col md="6">
                            <div className="d-flex align-items-start">
                              <Icon name="user" className="text-primary me-2 mt-1"></Icon>
                              <div>
                                <span className="sub-text d-block">Nom</span>
                                <span className="caption-text">{viewUser.last_name || "N/A"}</span>
                              </div>
                            </div>
                          </Col>
                          <Col md="12">
                            <div className="d-flex align-items-start">
                              <Icon name="mail" className="text-primary me-2 mt-1"></Icon>
                              <div>
                                <span className="sub-text d-block">Email</span>
                                <span className="caption-text">{viewUser.email || "N/A"}</span>
                              </div>
                            </div>
                          </Col>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Rôle et sites */}
                  <Col md="6">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-4">
                        <h6 className="title mb-3">
                          <Icon name="setting-alt" className="me-2"></Icon>
                          Rôle
                        </h6>
                        <div className="d-flex align-items-center">
                          {getRoleBadge(viewUser.roles)}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-4">
                        <h6 className="title mb-3">
                          <Icon name="location" className="me-2"></Icon>
                          Sites
                        </h6>
                        {viewUser.sites && viewUser.sites.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2">
                            {viewUser.sites.map((site, index) => (
                              <Badge key={index} color="info" className="badge-dim">
                                {site.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-soft">Aucun site assigné</span>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Dates */}
                  {(viewUser.created_at || viewUser.updated_at) && (
                    <Col md="12">
                      <div className="card border-0 bg-light">
                        <div className="card-body p-4">
                          <h6 className="title mb-3">
                            <Icon name="calendar" className="me-2"></Icon>
                            Historique
                          </h6>
                          <div className="row g-3">
                            {viewUser.created_at && (
                              <Col md="6">
                                <div className="d-flex align-items-start">
                                  <Icon name="plus-circle" className="text-success me-2 mt-1"></Icon>
                                  <div>
                                    <span className="sub-text d-block">Date de création</span>
                                    <span className="caption-text">
                                      {new Date(viewUser.created_at).toLocaleDateString('fr-FR', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                </div>
                              </Col>
                            )}
                            {viewUser.updated_at && (
                              <Col md="6">
                                <div className="d-flex align-items-start">
                                  <Icon name="edit" className="text-primary me-2 mt-1"></Icon>
                                  <div>
                                    <span className="sub-text d-block">Dernière modification</span>
                                    <span className="caption-text">
                                      {new Date(viewUser.updated_at).toLocaleDateString('fr-FR', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                </div>
                              </Col>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                  <Button
                    className="me-2"
                    color="light"
                    onClick={() => setModal({ view: false })}
                  >
                    Fermer
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => {
                      setModal({ view: false });
                      onEditClick(viewUser.id);
                    }}
                  >
                    <Icon name="edit"></Icon>
                    <span>Modifier</span>
                  </Button>
                </div>
              </ModalBody>
            </>
          )}
        </Modal>

        {/* Modal de confirmation de suppression */}
        <Modal isOpen={deleteModal} toggle={onDeleteCancel} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">Confirmer la suppression</h5>
            <button className="close" onClick={onDeleteCancel}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            {userToDelete && (
              <div className="text-center">
                <div className="mb-3">
                  <Icon name="alert-circle" className="text-danger" style={{ fontSize: "48px" }}></Icon>
                </div>
                <h6 className="mb-3">
                  Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>"{userToDelete.first_name} {userToDelete.last_name}"</strong> ?
                </h6>
                <p className="text-soft small">
                  Cette action est irréversible. Toutes les données associées à cet utilisateur seront également supprimées.
                </p>
              </div>
            )}
            <div className="text-end mt-4">
              <Button color="light" onClick={onDeleteCancel} className="me-2">
                Annuler
              </Button>
              <Button color="danger" onClick={onDeleteConfirm}>
                Supprimer
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Modal mot de passe généré */}
        <Modal
          isOpen={generatedPasswordModal.open}
          toggle={() => setGeneratedPasswordModal({ open: false, userName: "", password: "" })}
          className="modal-dialog-centered"
        >
          <div className="modal-header">
            <h5 className="modal-title">Mot de passe généré</h5>
            <button
              className="close"
              onClick={() => setGeneratedPasswordModal({ open: false, userName: "", password: "" })}
            >
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            <p className="mb-2">
              Nouveau mot de passe pour <strong>{generatedPasswordModal.userName}</strong> :
            </p>
            <div className="p-3 rounded border bg-light mb-3">
              <code style={{ fontSize: "1rem" }}>{generatedPasswordModal.password}</code>
            </div>
            <p className="text-soft small mb-4">
              Partagez ce mot de passe de manière sécurisée avec l&apos;utilisateur. Il pourra ensuite le modifier à la
              première connexion.
            </p>
            <div className="text-end">
              <Button color="light" className="me-2" onClick={() => setGeneratedPasswordModal({ open: false, userName: "", password: "" })}>
                Fermer
              </Button>
              <Button color="primary" onClick={onCopyGeneratedPassword}>
                <Icon name="copy"></Icon>
                <span>Copier le mot de passe</span>
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Modal de confirmation génération de mot de passe */}
        <Modal
          isOpen={generatePasswordConfirmModal.open}
          toggle={onGeneratePasswordCancel}
          className="modal-dialog-centered"
        >
          <div className="modal-header">
            <h5 className="modal-title">Confirmer la génération</h5>
            <button className="close" onClick={onGeneratePasswordCancel}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            <p className="mb-4">
              Générer un nouveau mot de passe pour{" "}
              <strong>
                {`${generatePasswordConfirmModal.user?.first_name || ""} ${
                  generatePasswordConfirmModal.user?.last_name || ""
                }`.trim() || generatePasswordConfirmModal.user?.email || "cet utilisateur"}
              </strong>{" "}
              ?
            </p>
            <div className="text-end">
              <Button color="light" className="me-2" onClick={onGeneratePasswordCancel}>
                Annuler
              </Button>
              <Button color="primary" onClick={onGeneratePasswordConfirm} disabled={!!generatingPasswordUserId}>
                {generatingPasswordUserId ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Génération...
                  </>
                ) : (
                  "Confirmer"
                )}
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default UserManagementPage;
